// Performance measurement
import { PerformanceTiming, startMemoryMonitor } from '@/utils/performance-timing';
import { MotionConfig } from 'framer-motion';

// Mark: App bundle started parsing
PerformanceTiming.mark('app-start');

if (import.meta.env.DEV) {
  console.time('🔍 BUNDLE-PARSE');
  window.addEventListener('DOMContentLoaded', () => {
    console.timeEnd('🔍 BUNDLE-PARSE');
    console.time('🔍 REACT-RENDER');
  });
}

// Force deployment update - $(date)
import { createBrowserRouter, RouterProvider, Navigate, useParams } from 'react-router-dom';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { Persister, PersistedClient } from '@tanstack/query-persist-client-core';
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { installGlobalTapHaptics } from '@/lib/haptics';
import { applyPreferredTextZoom } from '@/lib/nativeTextZoom';
import { MobileLayout } from '@/components/layout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { RootRedirect } from '@/components/auth/RootRedirect';
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import { clubJoinLinkService } from '@/services/api/club-join-link.service';
import { OnlineStatusProvider } from '@/contexts/OnlineStatusContext';
import { DashboardFiltersProvider } from '@/contexts/DashboardFiltersContext';
import { NotificationsProvider } from '@/contexts/NotificationsContext';
import { ErrorBoundary } from '@/components/ui/error-boundary';
import { PerformanceDebugger } from '@/components/debug/PerformanceDebugger';
import { SafariWarningBanner } from '@/components/ui/safari-warning-banner';
import { AuthLoadingSkeleton } from '@/components/ui/auth-loading-skeleton';
import { SplashScreen } from '@/components/ui/splash-screen';
import { PremiumLoadingScreen } from '@/components/round-completion/PremiumLoadingScreen';
import { WebVitalsTracker } from '@/components/monitoring/WebVitalsTracker';
import { GlobalErrorMonitor } from '@/components/monitoring/GlobalErrorMonitor';
import '@/utils/test-error-tracking'; // Load error tracking test utility
import '@/lib/toast-override'; // Mobile haptic feedback for toasts
import { STRIPE_CONFIG } from '@/services/domains/stripe/stripe-payment.service';
import { MEMBER_FEATURES } from '@/config/features';

// SECURITY: Validate Stripe environment on startup
STRIPE_CONFIG.validateEnvironment();

// Redirect /club-admin/member-profile/:memberId → /club-admin/member-progress/:memberId
const MemberProfileRedirect: React.FC = () => {
  const { memberId } = (useParams as any)();
  return <Navigate to={`/club-admin/member-progress/${memberId}`} replace />;
};
// Keep synchronous for immediate routes
const Index = lazy(() => import('@/pages/Index'));
import NotFound from '@/pages/NotFound';

// Lazy load all other route components
const Auth = lazy(() => import('@/pages/Auth'));
const ResetPasswordSimple = lazy(() => import('@/pages/ResetPasswordSimple'));
const ConfirmReset = lazy(() => import('@/pages/ConfirmReset'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const ScoreEntry = lazy(() => import('@/pages/ScoreEntry'));
const Leaderboard = lazy(() => import('@/pages/Leaderboard'));
const ProfileV2 = lazy(() => import('@/pages/ProfileV2'));
const ProfileSettingsPage = lazy(() => import('@/pages/ProfileSettingsPage'));
const PublicProfile = lazy(() => import('@/pages/PublicProfile'));
const InviteFriends = lazy(() => import('@/pages/InviteFriends'));
const Social = lazy(() => import('@/pages/Social'));
const PlayNew = lazy(() => import('@/pages/PlayNew'));
const AddPastRound = lazy(() => import('@/pages/AddPastRound'));
const PlaySolo = lazy(() => import('@/pages/PlaySolo'));
const ClubCourseSelection = lazy(() => import('@/pages/ClubCourseSelection'));
const CreateMultiplayerGame = lazy(() => import('@/pages/CreateMultiplayerGame'));
const MultiplayerGameLobby = lazy(() => import('@/pages/MultiplayerGameLobby'));
const UnifiedSoloScoreEntry = lazy(() => import('@/pages/UnifiedSoloScoreEntry'));
const UnifiedMultiplayerScoreEntry = lazy(() => import('@/pages/UnifiedMultiplayerScoreEntry'));
const UnifiedRoundCompletion = lazy(() => import('@/pages/UnifiedRoundCompletion'));
const SoloRoundCompletionUnified = lazy(() => import('@/pages/SoloRoundCompletionUnified'));
const MultiplayerGameCompletionUnified = lazy(() => import('@/pages/MultiplayerGameCompletionUnified'));
const SoloRoundCelebration = lazy(() => import('@/pages/SoloRoundCelebration'));
const SoloScoreDesignPreview = lazy(() => import('@/pages/play/SoloScoreDesignPreview'));
const MultiplayerScorePreview = lazy(() => import('@/pages/MultiplayerScorePreview'));
const TournamentScoreEntry = lazy(() => import('@/pages/TournamentScoreEntry').then(m => ({ default: m.TournamentScoreEntry })));
const TournamentRoundCompletion = lazy(() => import('@/pages/TournamentRoundCompletion'));
const JoinMultiplayerGame = lazy(() => import('@/pages/JoinMultiplayerGame'));
const JoinViaLink = lazy(() => import('@/pages/JoinViaLink'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
// Demo pages removed for App Store compliance
const PublicRoundAnalyticsView = lazy(() => import('@/components/analytics/PublicRoundAnalyticsView').then(m => ({ default: m.PublicRoundAnalyticsView })));
const ActivityAnalytics = lazy(() => import('@/pages/analytics/ActivityAnalytics'));
const ScoringAnalytics = lazy(() => import('@/pages/analytics/ScoringAnalytics'));
const MetricAnalytics = lazy(() => import('@/pages/analytics/MetricAnalytics'));
const MetricDrilldownPage = lazy(() => import('@/pages/analytics/MetricDrilldownPage'));
const SimulatorIndexPage = lazy(() => import('@/pages/analytics/SimulatorIndexPage'));
const DetailedClubAnalysisPage = lazy(() => import('@/pages/analytics/DetailedClubAnalysisPage'));
const ProfileStatistics = lazy(() => import('@/pages/ProfileStatistics'));
const ProfileTournaments = lazy(() => import('@/pages/ProfileTournaments'));
const ProfileCourses = lazy(() => import('@/pages/ProfileCourses'));
const ProfileClubs = lazy(() => import('@/pages/ProfileClubs'));
const TrophyRoom = lazy(() => import('@/pages/TrophyRoom'));
const ProfileActivities = lazy(() => import('@/pages/ProfileActivities'));
const ProfilePosts = lazy(() => import('@/pages/ProfilePosts'));
const PerformanceZones = lazy(() => import('@/pages/PerformanceZones'));
const ProfileFeedback = lazy(() => import('@/pages/ProfileFeedback'));
const FeedbackDetail = lazy(() => import('@/pages/FeedbackDetail'));

// Keep non-lazy (used in every protected route)
import { MemberAppTracker } from '@/components/analytics/MemberAppTracker';
// import QuestSystemDebug from '@/pages/QuestSystemDebug';
const CourseLeaderboard = lazy(() => import('@/pages/CourseLeaderboard'));
const CourseHub = lazy(() => import('@/pages/CourseHub'));
const CoursePreviewPage = lazy(() => import('@/pages/CoursePreviewPage'));
const ClubDetails = lazy(() => import('@/pages/ClubDetails'));
const BrowseClubs = lazy(() => import('@/pages/BrowseClubs'));
const ClubHub = lazy(() => import('@/pages/ClubHub'));
const ClubHubNew = lazy(() => import('@/pages/ClubHubNew'));
const MemberDirectory = lazy(() => import('@/pages/club/MemberDirectory'));
const PracticeBoardPage = lazy(() => import('@/pages/club/PracticeBoardPage'));
const ClubMembershipManagement = lazy(() => import('@/pages/club/ClubMembershipManagement'));
const ClubActivity = lazy(() => import('@/pages/ClubActivity'));
const CoachFeedbackPage = lazy(() => import('@/pages/CoachFeedbackPage'));
const CoachDashboardPage = lazy(() => import('@/pages/CoachDashboardPage'));
const CoachStudentDetailPage = lazy(() => import('@/pages/CoachStudentDetailPage'));
const CoachMyProfilePage = lazy(() => import('@/pages/CoachMyProfilePage'));
const ClubAdminCoachingSettings = lazy(() => import('@/pages/ClubAdminCoachingSettings'));
const ClubAdminCoachingRevenue = lazy(() => import('@/pages/ClubAdminCoachingRevenue'));
const ClubAdminSettingsCoaches = lazy(() => import('@/pages/ClubAdminSettingsCoaches'));
const PlayerCoachingPage = lazy(() => import('@/pages/PlayerCoachingPage'));
const FindCoachPage = lazy(() => import('@/pages/FindCoachPage'));
const CoachingTheoryPage = lazy(() => import('@/pages/CoachingTheoryPage'));

import { CoachPortalProvider } from '@/layouts/CoachLayout';

// Independent Coach Portal Pages
// CoachPortalDashboard removed — /coach-portal/dashboard redirects to /coach-portal/schedule
const CoachPortalProfile = lazy(() => import('@/pages/coach-portal/CoachProfile'));
const CoachPortalMyStudents = lazy(() => import('@/pages/coach-portal/CoachMyStudents'));
const CoachPortalStudentDetail = lazy(() => import('@/pages/coach-portal/CoachStudentDetail'));
const CoachPortalSettings = lazy(() => import('@/pages/coach-portal/CoachSettings'));
const CoachPortalPricing = lazy(() => import('@/pages/coach-portal/CoachPricing'));
const CoachPortalAffiliations = lazy(() => import('@/pages/coach-portal/CoachAffiliations'));
const CoachPortalSearchClubs = lazy(() => import('@/pages/coach-portal/SearchClubs'));
const CoachPortalMessages = lazy(() => import('@/pages/coach-portal/CoachMessages'));
const CoachPortalSessions = lazy(() => import('@/pages/coach-portal/CoachSessions'));
const CoachPortalSchedule = lazy(() => import('@/pages/coach-portal/CoachSchedule'));
const CoachPortalLibrary = lazy(() => import('@/pages/coach-portal/CoachLibrary'));
const CalendlyCallbackPage = lazy(() => import('@/pages/CalendlyCallbackPage'));
const StripeCallbackPage = lazy(() => import('@/pages/StripeCallbackPage'));
const CoachStripeCallbackPage = lazy(() => import('@/pages/coach-portal/CoachStripeCallbackPage'));
const ClubLeaderboard = lazy(() => import('@/pages/ClubLeaderboard'));
const ClubLeaderboardActivity = lazy(() => import('@/pages/ClubLeaderboardActivity'));
// REMOVED: Versioned leaderboard files (Batch 1.4, Task 1.4.2)
// Deleted V1, V2, V3, Refined1-3 variants - using only current versions above
const TeeBookingPage = lazy(() => import('@/pages/TeeBookingPage'));
const MemberServicesPage = lazy(() => import('@/components/member-services/MemberServicesPage'));
const CourseRecords = lazy(() => import('@/pages/CourseRecords'));
const Search = lazy(() => import('@/pages/Search'));
const Notifications = lazy(() => import('@/pages/Notifications'));
const MessagesPage = lazy(() => import('@/pages/MessagesPage').then(m => ({ default: m.MessagesPage })));
import { ChatInterface } from '@/components/messaging/ChatInterface';
import { MessagingErrorBoundary } from '@/components/messaging/MessagingErrorBoundary';
import { DrillDownProvider } from '@/contexts/DrillDownContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { DrillDownService } from '@/services/api/drill-down.service';

const CourseRoundHistory = lazy(() => import('@/pages/CourseRoundHistory'));
const CourseAnalytics = lazy(() => import('@/pages/CourseAnalytics'));
const EnhancedBoldDashboard = lazy(() => import('@/pages/EnhancedBoldDashboard'));
const EventsManagement = lazy(() => import('@/pages/EventsManagement'));
const EventManagement = lazy(() => import('@/pages/EventManagement'));
const EventAnalytics = lazy(() => import('@/pages/EventAnalytics'));
const LeagueManagementPage = lazy(() => import('@/pages/LeagueManagementPage').then(m => ({ default: m.LeagueManagementPage })));
const TournamentBracketPage = lazy(() => import('@/pages/TournamentBracket'));
const MultiRoundComponentShowcase = lazy(() => import('@/pages/MultiRoundComponentShowcase'));
const CourseRecordsVariations = lazy(() => import('@/pages/CourseRecordsVariations'));
const OverallChampionship = lazy(() => import('@/pages/leaderboard/OverallChampionship'));
const MostActive = lazy(() => import('@/pages/leaderboard/MostActive'));
const UnderHandicap = lazy(() => import('@/pages/leaderboard/UnderHandicap'));
const PlayingStreaks = lazy(() => import('@/pages/leaderboard/PlayingStreaks'));
// Removed broken imports - components don't exist
const ClubChatPage = lazy(() => import('@/pages/community/ClubChatPage'));
import { LeaderboardProfileTest } from '@/components/test/LeaderboardProfileTest';
import { ClubProvider } from '@/contexts/ClubContext';
import { PerformanceZonesProvider } from '@/contexts/PerformanceZonesContext';
import { RootLayout } from '@/components/layout/RootLayout';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

// Lazy load member import components to avoid circular dependencies
const AccountClaimingForm = lazy(() => import('@/components/member-import/AccountClaimingForm').then(m => ({ default: m.AccountClaimingForm })));
const JoinClubForm = lazy(() => import('@/components/member-import/JoinClubForm').then(m => ({ default: m.JoinClubForm })));
// Demo/landing pages removed for App Store compliance - use separate landing project at /landing directory


// Import the unified chat interface
import { UnifiedChatInterface } from './components/messaging/UnifiedChatInterface';

function ChatRoute() {
  const { state } = useLocation();
  const s = state as { initialMessageText?: string; pendingExerciseData?: Record<string, unknown> } | null;
  return (
    <UnifiedChatInterface
      initialMessageText={s?.initialMessageText}
      pendingExerciseData={s?.pendingExerciseData}
    />
  );
}

// Import new pages
const FriendsPage = lazy(() => import('@/pages/FriendsPage'));

// What's New announcement modal - moved to RootLayout for router context
const Admin = lazy(() => import('@/pages/Admin'));
import { ClubAdminLayout } from '@/components/admin/ClubAdminLayout';
const ClubAdminDashboard = lazy(() => import('@/pages/ClubAdminDashboard'));
const ClubAdminSettings = lazy(() => import('@/pages/ClubAdminSettings'));
const OperatingHoursScraper = lazy(() => import('@/components/admin/OperatingHoursScraper'));
const ClubAdminCourses = lazy(() => import('@/pages/ClubAdminCourses'));
const ClubAdminChannels = lazy(() => import('@/pages/ClubAdminChannels'));
const ClubAdminComms = lazy(() => import('@/pages/ClubAdminComms'));
const ChannelInsights = lazy(() => import('@/pages/ChannelInsights'));
const ClubAdminAnnouncements = lazy(() => import('@/pages/ClubAdminAnnouncements'));
const ClubAdminAnnouncementAnalytics = lazy(() => import('@/pages/ClubAdminAnnouncementAnalytics'));
const ClubAdminFeedback = lazy(() => import('@/pages/ClubAdminFeedback'));
const ClubAdminMembers = lazy(() => import('@/pages/ClubAdminMembers'));
const ClubAdminMemberProgress = lazy(() => import('@/pages/ClubAdminMemberProgress'));
const ClubAdminMemberProgressV2 = lazy(() => import('@/pages/ClubAdminMemberProgressV2'));
const ClubAdminMemberProgressDetail = lazy(() => import('@/pages/ClubAdminMemberProgressDetail'));
const ClubAdminAnalytics = lazy(() => import('@/pages/ClubAdminAnalytics'));
const ClubAdminTeeSheet = lazy(() => import('@/pages/ClubAdminTeeSheet'));
const ClubAdminTeeCheckIn = lazy(() => import('@/pages/ClubAdminTeeCheckIn'));
const ClubAdminTeeGenerate = lazy(() => import('@/pages/ClubAdminTeeGenerate'));
const ClubAdminTeeSettings = lazy(() => import('@/pages/ClubAdminTeeSettings'));
const ClubAdminTeeSheetAnalytics = lazy(() => import('@/pages/ClubAdminTeeSheetAnalytics'));
const ClubAdminServices = lazy(() => import('@/pages/ClubAdminServices'));
const ClubAdminServicesCarts = lazy(() => import('@/pages/ClubAdminServicesCarts'));
const ClubAdminServicesLockers = lazy(() => import('@/pages/ClubAdminServicesLockers'));
const ClubAdminServicesConfig = lazy(() => import('@/pages/ClubAdminServicesConfig'));
const ClubAdminMembershipsV3 = lazy(() => import('@/pages/ClubAdminMembershipsV3'));
const ClubAdminRevenue = lazy(() => import('@/pages/ClubAdminRevenue'));
const ClubAdminTransactions = lazy(() => import('@/pages/ClubAdminTransactions'));
const ClubAdminPaymentSettings = lazy(() => import('@/pages/ClubAdminPaymentSettings'));

// Reports Hub
const ClubAdminReports = lazy(() => import('@/pages/ClubAdminReports'));
const ClubAdminReportsMembers = lazy(() => import('@/pages/ClubAdminReportsMembers'));
const ClubAdminReportsRevenue = lazy(() => import('@/pages/ClubAdminReportsRevenue'));
const ClubAdminReportsNewMemberSuccess = lazy(() => import('@/pages/ClubAdminReportsNewMemberSuccess'));
const ClubAdminReportsTeeSheet = lazy(() => import('@/pages/ClubAdminReportsTeeSheet'));
const ClubAdminReportsCommunications = lazy(() => import('@/pages/ClubAdminReportsCommunications'));
const ClubAdminReportsServices = lazy(() => import('@/pages/ClubAdminReportsServices'));
const ClubAdminReportsEvents = lazy(() => import('@/pages/ClubAdminReportsEvents'));
const ClubEvents = lazy(() => import('@/pages/ClubEvents'));
const ClubEventDetail = lazy(() => import('@/pages/ClubEventDetail'));
const TournamentDetail = lazy(() => import('@/pages/TournamentDetail'));
const MemberEventHistory = lazy(() => import('@/pages/MemberEventHistory'));
const MemberExpenses = lazy(() => import('@/pages/MemberExpenses'));
const RedirectToClubEvents = lazy(() => import('@/components/RedirectToClubEvents'));
const EventDetails = lazy(() => import('@/pages/EventDetails'));
const EventRegistrations = lazy(() => import('@/pages/EventRegistrations'));
const EventCommunications = lazy(() => import('@/pages/EventCommunications'));
const OldMemberProfilePage = lazy(() => import('@/pages/OldMemberProfilePage').then(m => ({ default: m.OldMemberProfilePage })));

// Import staff management pages
const ClubAdminStaff = lazy(() => import('@/pages/ClubAdminStaff'));
const ClubAdminStaffPeople = lazy(() => import('@/pages/ClubAdminStaffPeople'));
const StaffDashboardPage = lazy(() => import('@/pages/StaffDashboardPage'));

// Import new tournament management components
const EventManagementDashboard = lazy(() => import('@/pages/EventManagementDashboard'));
const TournamentStructureManager = lazy(() => import('@/pages/TournamentStructureManager'));
const AdminScoreManager = lazy(() => import('@/pages/AdminScoreManager'));
const MatchResolutionCenter = lazy(() => import('@/pages/MatchResolutionCenter'));
const LiveTournamentVisualization = lazy(() => import('@/pages/LiveTournamentVisualization'));
const GolfEventDetail = lazy(() => import('@/pages/GolfEventDetail'));
const GroupPhaseManager = lazy(() => import('@/pages/GroupPhaseManager'));
const TeeSheet = lazy(() => import('@/pages/TeeSheet'));
const MultiRoundLeaderboard = lazy(() => import('@/pages/MultiRoundLeaderboard'));
const UnifiedTournamentPage = lazy(() => import('@/pages/UnifiedTournamentPage'));
const TournamentRedirect = lazy(() => import('@/components/tournament/TournamentRedirect'));
const MyPracticeMockup = lazy(() => import('@/pages/MyPracticeMockup'));
const PracticePage = lazy(() => import('@/pages/PracticePage'));
const PracticeExercisePage = lazy(() => import('@/pages/PracticeExercisePage'));
const CreateSessionPage = lazy(() => import('@/pages/CreateSessionPage'));
const OnboardingFlow = lazy(() => import('@/components/onboarding/OnboardingFlow').then(m => ({ default: m.OnboardingFlow })));

// Layout wrapper component for protected routes
const LayoutWrapper = ({ children }: { children: React.ReactNode }) => (
  <>
    <MemberAppTracker />
    <MobileLayout>{children}</MobileLayout>
  </>
);

// Helper function to create protected routes with layout and Suspense
const createProtectedRoute = (path: string, Component: React.ComponentType) => ({
  path,
  element: (
    <ProtectedRoute>
      <Suspense fallback={<AuthLoadingSkeleton />}>
        <LayoutWrapper>
          <Component />
        </LayoutWrapper>
      </Suspense>
    </ProtectedRoute>
  ),
});

// Score entry routes use the scorecard loading screen instead of the generic skeleton
const createScoreEntryRoute = (path: string, Component: React.ComponentType) => ({
  path,
  element: (
    <ProtectedRoute>
      <Suspense fallback={<PremiumLoadingScreen variant="scorecard" />}>
        <LayoutWrapper>
          <Component />
        </LayoutWrapper>
      </Suspense>
    </ProtectedRoute>
  ),
});

// Club chat component (ClubProvider now available app-wide)
const ClubChatComponent = ClubChatPage;

// Helper function to create public routes with layout and Suspense
const createPublicRoute = (path: string, Component: React.ComponentType) => ({
  path,
  element: (
    <Suspense fallback={<AuthLoadingSkeleton />}>
      <LayoutWrapper>
        <Component />
      </LayoutWrapper>
    </Suspense>
  ),
});

// Claim Account Page Component
const ClaimAccountPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [onboardingState, setOnboardingState] = useState<{
    active: boolean;
    prefill: {
      firstName?: string;
      lastName?: string;
      handicap?: number | null;
      clubMembership?: string;
      clubId?: string;
    };
  }>({ active: false, prefill: {} });

  if (!token) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: 'linear-gradient(135deg, #52B788 0%, #2D5A3D 50%, #1B4332 100%)' }}
      >
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-bold text-white">Invalid Link</h1>
          <p className="text-white/70">This invitation link is invalid or expired.</p>
          <button
            onClick={() => navigate('/auth')}
            className="mt-4 bg-white text-[#1B4332] font-semibold px-6 py-3 rounded-xl hover:bg-white/90 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (onboardingState.active) {
    return (
      <Suspense fallback={<SplashScreen />}>
        <OnboardingFlow
          userType="member"
          prefillData={onboardingState.prefill}
          skipScreens={['club']}
          onComplete={() => navigate('/practice')}
          onSkip={() => navigate('/practice')}
        />
      </Suspense>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: 'linear-gradient(135deg, #52B788 0%, #2D5A3D 50%, #1B4332 100%)',
        paddingTop: 'max(env(safe-area-inset-top), 24px)',
        paddingBottom: 'max(env(safe-area-inset-bottom), 24px)',
        paddingLeft: 'max(env(safe-area-inset-left), 16px)',
        paddingRight: 'max(env(safe-area-inset-right), 16px)',
      }}
    >
      {/* Logo / brand mark */}
      <div className="flex justify-center pb-6 pt-2">
        <img
          src="/images/brand-logos/__White Icon.png"
          alt="Strokone"
          className="h-10 w-auto opacity-90"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      </div>

      <div className="flex-1 flex items-start justify-center">
        <div className="w-full max-w-sm">
          <Suspense fallback={null}>
            <AccountClaimingForm
              token={token}
              onSuccess={async (result) => {
            // Sign in with the credentials the member just set
            const { error: signInError } = await supabase.auth.signInWithPassword({
              email: result.email,
              password: result.password
            });

            if (signInError) {
              // Fallback — redirect to login if auto sign-in fails
              navigate('/auth', {
                state: {
                  message: 'Account created! Please sign in with your new credentials.',
                  email: result.email
                }
              });
              return;
            }

            // Auto sign-in worked — launch onboarding with pre-filled data
            setOnboardingState({
              active: true,
              prefill: {
                firstName: result.firstName,
                lastName: result.lastName,
                handicap: result.handicap,
              }
            });
          }}
          onError={(error) => {
            console.error('Account claiming error:', error);
          }}
        />
          </Suspense>
        </div>
      </div>
    </div>
  );
};

// Handles pending QR token after user logs in
const PendingQRClaimHandler: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('pendingQRClubToken');
    if (!token) return;
    localStorage.removeItem('pendingQRClubToken');

    clubJoinLinkService.claimJoinLink(token)
      .then(result => {
        if (result.success && !result.already_member) {
          toast.success(`Welcome to ${result.club_name}!`);
          queryClient.invalidateQueries({ queryKey: ['club-memberships'] });
        }
      })
      .catch(() => {
        // Silent — user can retry by scanning again
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return null;
};

// Handles /join-club?token=… QR deep links
const JoinClubViaQRPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [clubName, setClubName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) return;
    if (!user) {
      localStorage.setItem('pendingQRClubToken', token);
      navigate('/auth');
      return;
    }
    setStatus('loading');
    clubJoinLinkService.claimJoinLink(token)
      .then(result => {
        setClubName(result.club_name);
        setStatus('success');
      })
      .catch(e => {
        setErrorMsg(e.message ?? 'Something went wrong');
        setStatus('error');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?.id]);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Link</h1>
          <p className="text-gray-500 mb-4">This join link is not valid.</p>
          <button onClick={() => navigate('/auth')} className="bg-[#1B4332] text-white px-4 py-2 rounded-lg">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Joining club…</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Couldn't join</h1>
          <p className="text-gray-500 mb-4">{errorMsg}</p>
          <button onClick={() => navigate('/dashboard')} className="bg-[#1B4332] text-white px-4 py-2 rounded-lg">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">You're in!</h1>
        <p className="text-gray-500 mb-4">You've joined {clubName}.</p>
        <button onClick={() => navigate('/club')} className="bg-[#1B4332] text-white px-4 py-2 rounded-lg">
          Go to Club
        </button>
      </div>
    </div>
  );
};

// Join Club Page Component (email-invite flow — keep for JoinClubForm usage)
const JoinClubPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Invalid Link</h1>
          <p className="text-gray-600 mb-4">This invitation link is invalid or expired.</p>
          <button 
            onClick={() => navigate('/auth')}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <JoinClubForm 
        token={token}
        onSuccess={async (result) => {
          console.log('🎉 Club join successful!');
          // Redirect to profile or club page
          navigate('/profile');
        }}
        onError={(error) => {
          console.error('Club joining error:', error);
        }}
      />
    </div>
  );
};

// Route configuration
const routes = [
  {
    element: <RootLayout />,
    children: [
      {
        path: '/',
        element: <RootRedirect />
      },
  {
    path: '/auth',
    element: (
      <Suspense fallback={<AuthLoadingSkeleton />}>
        <Auth />
      </Suspense>
    )
  },
  {
    path: '/auth/club',
    element: (
      <Suspense fallback={<AuthLoadingSkeleton />}>
        <Auth />
      </Suspense>
    )
  },
  {
    path: '/auth/coach',
    element: (
      <Suspense fallback={<AuthLoadingSkeleton />}>
        <Auth />
      </Suspense>
    )
  },
  {
    path: '/confirm-reset',
    element: (
      <Suspense fallback={<AuthLoadingSkeleton />}>
        <ConfirmReset />
      </Suspense>
    )
  },
  {
    path: '/reset-password',
    element: (
      <Suspense fallback={<AuthLoadingSkeleton />}>
        <ResetPasswordSimple />
      </Suspense>
    )
  },
  {
    path: '/auth/callback',
    element: (
      <Suspense fallback={<AuthLoadingSkeleton />}>
        <AuthCallback />
      </Suspense>
    )
  },
  {
    path: '/claim-account',
    element: <ClaimAccountPage />
  },
  {
    path: '/join-club',
    element: <JoinClubViaQRPage />
  },
  // Demo routes removed for App Store compliance

  // Protected routes with layout
  ...([
    ['/dashboard', Dashboard],
    ['/tournament/:id/scores', ScoreEntry],
    ['/tournament/:tournamentId/round/:roundId/score', TournamentScoreEntry],
    ['/tournament/:tournamentId/round/:roundId/complete', TournamentRoundCompletion],
    ['/tournament/:id/leaderboard', Leaderboard],
    ['/play', PlayNew], // Primary play page with recent courses
    ['/play/add-past-round', AddPastRound],
    ['/play/clubs', BrowseClubs],
    ['/play/solo', PlaySolo],
    ['/play/solo/club/:clubId', ClubCourseSelection],
    ['/play/solo/:id/celebration', SoloRoundCelebration],
    ['/play/solo/:id/complete', SoloRoundCompletionUnified],
    ['/play/multiplayer/club/:clubId', ClubCourseSelection],
    ['/play/multiplayer/create', CreateMultiplayerGame],
    ['/play/multiplayer/:id/lobby', MultiplayerGameLobby],
    ['/play/multiplayer/:id/score-preview', MultiplayerScorePreview],
    ['/play/multiplayer/:id/complete', MultiplayerGameCompletionUnified],
    ['/play/multiplayer/join', JoinMultiplayerGame],
    ['/play/multiplayer/join/:code', JoinMultiplayerGame],
    ['/join/:token', JoinViaLink],
    ['/profile/settings', ProfileSettingsPage],
    ['/profile', ProfileV2],
    ['/profile/:userId', PublicProfile],
    ['/profile/:userId/activities', ProfileActivities],
    ['/profile/:userId/statistics', ProfileStatistics],
    ['/profile/:userId/tournaments', ProfileTournaments],
    ['/profile/:userId/courses', ProfileCourses],
    ['/profile/:userId/performance-zones', PerformanceZones],
    ['/profile/:userId/trophy-room', TrophyRoom],
    ['/profile/:userId/progression', ClubActivity],
    ['/profile/:userId/posts', ProfilePosts],
    ['/profile/:userId/clubs', ProfileClubs],
    ['/profile/:userId/friends', FriendsPage],
    ['/profile/invite-friends', InviteFriends],
    ['/profile/statistics', ProfileStatistics],
    ['/profile/tournaments', ProfileTournaments],
    ['/profile/trophy-room', TrophyRoom],
    ['/profile/courses', ProfileCourses],
    ['/profile/activities', ProfileActivities],
    ['/profile/posts', ProfilePosts],
    ['/profile/performance-zones', PerformanceZones],
    ['/profile/friends', FriendsPage],
    ['/profile/feedback', ProfileFeedback],
    ['/profile/expenses', MemberExpenses],
    ['/feedback/:id', FeedbackDetail],
    // ['/test/quest-system', QuestSystemDebug],
    ['/course/:courseId', CourseHub],
    ['/course/:courseId/preview', CoursePreviewPage],
    ['/course/:courseId/preview/:holeNumber', CoursePreviewPage],
    ['/course/:courseId/leaderboards', CourseLeaderboard],
    ['/club/:clubId/details', ClubDetails],
    ['/course/:courseId/leaderboards/:categoryId', CourseLeaderboard],
    ['/course/:courseId/records/:recordType', CourseLeaderboard],
    ['/profile/clubs', ProfileClubs],
    ['/index', Index],
    ['/club', ClubHubNew],
    ['/club-old', ClubHub],
    ['/progression', ClubActivity],
    ['/club/activity', ClubActivity],
    ['/my-practice-mockup', MyPracticeMockup],
    ['/practice', PracticePage],
    ['/practice/:exerciseId', PracticeExercisePage],
    // Player coaching (Coach tab). Inside RootLayout so the feature tour and
    // push initialiser stay mounted when the player switches tabs.
    ['/coaching', PlayerCoachingPage],
    ['/coaching/theory', CoachingTheoryPage],
    ['/coaching/find-coach', FindCoachPage],
    ['/create-session', CreateSessionPage],
    ['/club/activity/coach-feedback', CoachFeedbackPage],
    ['/club/events', RedirectToClubEvents], // Backward compatibility redirect
    ['/club/:clubId/events', ClubEvents],
    ['/club/:clubId/events/:eventId', ClubEventDetail],
    ['/club/:clubId/events/:eventId/tournament', TournamentDetail],
    ['/club/:clubId/my-events', MemberEventHistory],
    ['/club/:clubId/members', MemberDirectory],
    ['/club/:clubId/practice-board', PracticeBoardPage],
    ['/clubs/manage', ClubMembershipManagement],

    ['/leaderboard', ClubLeaderboard],
    ['/leaderboard-activity', ClubLeaderboardActivity],
    // REMOVED: Versioned leaderboard routes (Batch 1.4, Task 1.4.2)
    // All variant routes deleted - users redirected to current versions
    ['/leaderboard/overall-championship', OverallChampionship],
    ['/leaderboard/most-active', MostActive],
    ['/leaderboard/under-handicap', UnderHandicap],
    ['/leaderboard/playing-streaks', PlayingStreaks],
    ['/analytics/course-records', CourseRecords],
    ['/search', Search],
    ['/social', Social],
    ['/notifications', Notifications],
    ['/messages', () => <MessagingErrorBoundary><MessagesPage /></MessagingErrorBoundary>],
    ['/test/unified-chat/:conversationId', () => <UnifiedChatInterface />],
    ['/test/leaderboard-profile', () => <LeaderboardProfileTest />],


    ['/round/:roundId/analytics', () => <PublicRoundAnalyticsView />],
    ['/analytics/activity', ActivityAnalytics],
    ['/analytics/scoring', ScoringAnalytics],
    ['/metric-analysis/:metricType', MetricDrilldownPage],
    ['/metric-analysis/handicap/simulator-index', SimulatorIndexPage],
    ['/metric-analysis-detailed/clubs', DetailedClubAnalysisPage],
    ['/analytics/:metricType', MetricAnalytics],
    // Demo routes removed for App Store compliance
    ['/course/:courseId/rounds', CourseRoundHistory],
    ['/course/:courseId/analytics', CourseAnalytics],
    ['/dashboard/enhanced-bold', EnhancedBoldDashboard],
    ['/course-records/variations', CourseRecordsVariations],
            ['/community/chat/minimal', ClubChatComponent],
        ['/club/chat', ClubChatComponent],
    ['/admin', Admin],
    ['/tee-sheet', TeeSheet],
    ['/multi-round-leaderboard', MultiRoundLeaderboard],
    ['/book-tee-time', TeeBookingPage],
    ['/book-tee-time/:clubId', TeeBookingPage],
    ['/services', MemberServicesPage],
    ['/services/:clubId', MemberServicesPage],
      ] as const).map(([path, Component]) => createProtectedRoute(path, Component)),

  // Score entry routes use the scorecard loading screen
  createScoreEntryRoute('/play/solo/:id/score', UnifiedSoloScoreEntry),
  createScoreEntryRoute('/play/multiplayer/:id/score', UnifiedMultiplayerScoreEntry),

  // My Bookings page (needs custom props) — hidden when teeBooking feature is off
  ...(MEMBER_FEATURES.teeBooking ? [{
    path: '/my-bookings',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<AuthLoadingSkeleton />}>
          <LayoutWrapper>
            <TeeBookingPage view="my-bookings" />
          </LayoutWrapper>
        </Suspense>
      </ProtectedRoute>
    ),
  }] : [{ path: '/my-bookings', element: <Navigate to="/play" replace /> }]),

  // Public route with layout
  createPublicRoute('*', NotFound),
  
  // Chat interface without mobile layout (full screen)
  {
    path: '/messages/:conversationId',
    element: (
      <ProtectedRoute>
        <MessagingErrorBoundary showShell>
          <ChatRoute />
        </MessagingErrorBoundary>
      </ProtectedRoute>
    ),
  },
    ], // Close RootLayout children array
  }, // Close RootLayout route object

  // =====================================================
  // CLUB ADMIN ROUTES - All nested under single layout
  // The sidebar stays stable during navigation
  // =====================================================
  {
    path: '/club-admin',
    element: (
      <ProtectedRoute>
        <ClubAdminLayout />
      </ProtectedRoute>
    ),
    children: [
      // Default redirect to dashboard
      { index: true, element: <Navigate to="dashboard" replace /> },

      // Dashboard
      { path: 'dashboard', element: <Suspense fallback={null}><ClubAdminDashboard /></Suspense> },

      // Settings
      { path: 'settings', element: <Suspense fallback={null}><ClubAdminSettings /></Suspense> },
      { path: 'settings/coaches', element: <Suspense fallback={null}><ClubAdminSettingsCoaches /></Suspense> },

      // Operating Hours Scraper
      { path: 'operating-hours-scraper', element: <Suspense fallback={null}><OperatingHoursScraper /></Suspense> },

      // Courses
      { path: 'courses', element: <Suspense fallback={null}><ClubAdminCourses /></Suspense> },

      // Communication Hub
      { path: 'communication', element: <Suspense fallback={null}><ClubAdminComms /></Suspense> },
      // Legacy redirects
      { path: 'comms', element: <Navigate to="/club-admin/communication" replace /> },
      { path: 'channels', element: <Navigate to="/club-admin/communication" replace /> },
      { path: 'channels/insights', element: <Suspense fallback={null}><ChannelInsights /></Suspense> },

      // Announcements
      { path: 'announcements', element: <Suspense fallback={null}><ClubAdminAnnouncements /></Suspense> },
      { path: 'announcements/analytics', element: <Suspense fallback={null}><ClubAdminAnnouncementAnalytics /></Suspense> },

      // Feedback
      { path: 'feedback', element: <Suspense fallback={null}><ClubAdminFeedback /></Suspense> },

      // Members
      { path: 'members', element: <Suspense fallback={null}><ClubAdminMembers /></Suspense> },
      { path: 'members/:memberId', element: <Suspense fallback={null}><OldMemberProfilePage /></Suspense> },
      { path: 'members/memberships', element: <Suspense fallback={null}><ClubAdminMembershipsV3 /></Suspense> },
      { path: 'member-profile/:memberId', element: <MemberProfileRedirect /> },

      // Member Progress CRM
      { path: 'member-progress', element: <Suspense fallback={null}><ClubAdminMemberProgressV2 /></Suspense> },
      { path: 'member-progress/:memberId', element: <Suspense fallback={null}><ClubAdminMemberProgressDetail /></Suspense> },

      // Analytics
      { path: 'analytics', element: <Suspense fallback={null}><ClubAdminAnalytics /></Suspense> },

      // Tee Sheet
      { path: 'tee-sheet', element: <Suspense fallback={null}><ClubAdminTeeSheet /></Suspense> },
      { path: 'tee-sheet/check-in', element: <Suspense fallback={null}><ClubAdminTeeCheckIn /></Suspense> },
      { path: 'tee-sheet/generate', element: <Suspense fallback={null}><ClubAdminTeeGenerate /></Suspense> },
      { path: 'tee-sheet/settings', element: <Suspense fallback={null}><ClubAdminTeeSettings /></Suspense> },
      { path: 'tee-sheet/analytics', element: <Suspense fallback={null}><ClubAdminTeeSheetAnalytics /></Suspense> },

      // Carts redirect
      { path: 'carts', element: <Navigate to="services/carts" replace /> },

      // Services
      { path: 'services', element: <Suspense fallback={null}><ClubAdminServices /></Suspense> },
      { path: 'services/carts', element: <Suspense fallback={null}><ClubAdminServicesCarts /></Suspense> },
      { path: 'services/lockers', element: <Suspense fallback={null}><ClubAdminServicesLockers /></Suspense> },
      { path: 'services/config', element: <Suspense fallback={null}><ClubAdminServicesConfig /></Suspense> },

      // Revenue
      { path: 'revenue', element: <Suspense fallback={null}><ClubAdminRevenue /></Suspense> },
      { path: 'revenue/transactions', element: <Suspense fallback={null}><ClubAdminTransactions /></Suspense> },
      { path: 'revenue/settings', element: <Suspense fallback={null}><ClubAdminPaymentSettings /></Suspense> },

      // Staff
      { path: 'staff', element: <Suspense fallback={null}><ClubAdminStaff /></Suspense> },
      { path: 'staff/people', element: <Suspense fallback={null}><ClubAdminStaffPeople /></Suspense> },

      // Coaching
      { path: 'coaching', element: <Suspense fallback={null}><CoachDashboardPage /></Suspense> },
      { path: 'coaching/my-profile', element: <Suspense fallback={null}><CoachMyProfilePage /></Suspense> },
      { path: 'coaching/settings', element: <Suspense fallback={null}><ClubAdminCoachingSettings /></Suspense> },
      { path: 'coaching/revenue', element: <Suspense fallback={null}><ClubAdminCoachingRevenue /></Suspense> },
      { path: 'coaching/students/:playerId', element: <Suspense fallback={null}><CoachStudentDetailPage /></Suspense> },

      // Reports Hub
      { path: 'reports', element: <Suspense fallback={null}><ClubAdminReports /></Suspense> },
      { path: 'reports/members', element: <Suspense fallback={null}><ClubAdminReportsMembers /></Suspense> },
      { path: 'reports/revenue', element: <Suspense fallback={null}><ClubAdminReportsRevenue /></Suspense> },
      { path: 'reports/tee-sheet', element: <Suspense fallback={null}><ClubAdminReportsTeeSheet /></Suspense> },
      { path: 'reports/communications', element: <Suspense fallback={null}><ClubAdminReportsCommunications /></Suspense> },
      { path: 'reports/services', element: <Suspense fallback={null}><ClubAdminReportsServices /></Suspense> },
      { path: 'reports/events', element: <Suspense fallback={null}><ClubAdminReportsEvents /></Suspense> },
      { path: 'reports/new-member-success', element: <Navigate to="/club-admin/member-progress" replace /> },

      // Payment callbacks
      { path: 'payment-settings/stripe/callback', element: <Suspense fallback={null}><StripeCallbackPage /></Suspense> },
    ],
  },

  // Staff Dashboard (for staff members - NOT club-admin layout)
  {
    path: '/staff',
    element: (
      <ProtectedRoute>
        <Suspense fallback={null}>
          <StaffDashboardPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Calendly OAuth Callback (separate route)
  {
    path: '/coach/settings/calendly/callback',
    element: (
      <ProtectedRoute>
        <Suspense fallback={null}>
          <CalendlyCallbackPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // =====================================================
  // INDEPENDENT COACH PORTAL - Separate from club-admin
  // CoachPortalProvider wraps all routes so selectedClubId
  // context is shared across page navigations.
  // =====================================================
  {
    path: '/coach-portal',
    element: <Navigate to="/coach-portal/schedule" replace />,
  },
  {
    path: '/coach-portal/dashboard',
    element: <Navigate to="/coach-portal/schedule" replace />,
  },
  {
    path: '/coach-portal/profile',
    element: (
      <ProtectedRoute>
        <CoachPortalProvider>
          <Suspense fallback={null}>
            <CoachPortalProfile />
          </Suspense>
        </CoachPortalProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: '/coach-portal/my-students',
    element: (
      <ProtectedRoute>
        <CoachPortalProvider>
          <Suspense fallback={null}>
            <CoachPortalMyStudents />
          </Suspense>
        </CoachPortalProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: '/coach-portal/students/:studentId',
    element: (
      <ProtectedRoute>
        <CoachPortalProvider>
          <Suspense fallback={null}>
            <CoachPortalStudentDetail />
          </Suspense>
        </CoachPortalProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: '/coach-portal/settings',
    element: (
      <ProtectedRoute>
        <CoachPortalProvider>
          <Suspense fallback={null}>
            <CoachPortalSettings />
          </Suspense>
        </CoachPortalProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: '/coach-portal/settings/stripe/callback',
    element: (
      <ProtectedRoute>
        <Suspense fallback={null}>
          <CoachStripeCallbackPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/coach-portal/pricing',
    element: (
      <ProtectedRoute>
        <CoachPortalProvider>
          <Suspense fallback={null}>
            <CoachPortalPricing />
          </Suspense>
        </CoachPortalProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: '/coach-portal/affiliations',
    element: (
      <ProtectedRoute>
        <CoachPortalProvider>
          <Suspense fallback={null}>
            <CoachPortalAffiliations />
          </Suspense>
        </CoachPortalProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: '/coach-portal/messages',
    element: (
      <ProtectedRoute>
        <CoachPortalProvider>
          <Suspense fallback={null}>
            <CoachPortalMessages />
          </Suspense>
        </CoachPortalProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: '/coach-portal/sessions',
    element: (
      <ProtectedRoute>
        <CoachPortalProvider>
          <Suspense fallback={null}>
            <CoachPortalSessions />
          </Suspense>
        </CoachPortalProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: '/coach-portal/schedule',
    element: (
      <ProtectedRoute>
        <CoachPortalProvider>
          <Suspense fallback={null}>
            <CoachPortalSchedule />
          </Suspense>
        </CoachPortalProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: '/coach-portal/library',
    element: (
      <ProtectedRoute>
        <CoachPortalProvider>
          <Suspense fallback={null}>
            <CoachPortalLibrary />
          </Suspense>
        </CoachPortalProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: '/coach-portal/search-clubs',
    element: (
      <ProtectedRoute>
        <CoachPortalProvider>
          <Suspense fallback={null}>
            <CoachPortalSearchClubs />
          </Suspense>
        </CoachPortalProvider>
      </ProtectedRoute>
    ),
  },

  // =====================================================
  // CLUB ADMIN EVENTS - Nested for stable sidebar
  // =====================================================
  {
    path: '/club-admin/events',
    element: (
      <ProtectedRoute>
        <ClubAdminLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Suspense fallback={null}><EventsManagement /></Suspense> },
      { path: 'analytics', element: <Suspense fallback={null}><EventAnalytics /></Suspense> },
      { path: ':eventId', element: <Suspense fallback={null}><EventDetails /></Suspense> },
      { path: ':eventId/manage', element: <Suspense fallback={null}><EventManagement /></Suspense> },
      { path: ':eventId/league-management', element: <Suspense fallback={null}><LeagueManagementPage /></Suspense> },
      { path: ':eventId/tournament', element: <Suspense fallback={null}><UnifiedTournamentPage /></Suspense> },
      { path: ':eventId/registrations', element: <Suspense fallback={null}><EventRegistrations /></Suspense> },
      { path: ':eventId/communications', element: <Suspense fallback={null}><EventCommunications /></Suspense> },
      { path: ':eventId/bracket', element: <Navigate to="../tournament" replace /> },
      { path: ':eventId/structure-manager', element: <Suspense fallback={null}><TournamentStructureManager /></Suspense> },
      { path: ':eventId/admin-scores', element: <Suspense fallback={null}><AdminScoreManager /></Suspense> },
      { path: ':eventId/match-resolution', element: <Suspense fallback={null}><MatchResolutionCenter /></Suspense> },
      { path: ':eventId/live-tournament', element: <Navigate to="../tournament" replace /> },
      { path: ':eventId/group-phase', element: <Navigate to="../tournament" replace /> },
    ],
  },

  // Other club-admin routes with layout
  {
    path: '/club-admin/multi-round-showcase',
    element: (
      <ProtectedRoute>
        <ClubAdminLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Suspense fallback={null}><MultiRoundComponentShowcase /></Suspense> },
    ],
  },
  {
    path: '/club-admin/tournament-dashboard',
    element: (
      <ProtectedRoute>
        <ClubAdminLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Suspense fallback={null}><EventManagementDashboard /></Suspense> },
    ],
  },

  // Non club-admin routes
  {
    path: '/golf-event/:clubId/:eventId',
    element: (
      <ProtectedRoute>
        <Suspense fallback={null}>
          <GolfEventDetail />
        </Suspense>
      </ProtectedRoute>
    ),
  },
];

const router = createBrowserRouter(routes);

// Loading component for Suspense fallback
const AppLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-app">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#52B788] mx-auto mb-4" />
      <p className="text-gray-600">Loading application...</p>
    </div>
  </div>
);

// Wrapper component to provide drill-down context with auth access
const AppWithDrillDownProvider = () => {
  const drillDownService = new DrillDownService();

  // Global tap haptics — one listener gives every button/link a native-feeling tap,
  // including the many raw <button> elements that don't route through <Button>.
  useEffect(() => installGlobalTapHaptics(), []);

  // Follow the iPhone's Text Size setting, and pick up changes made while backgrounded.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    applyPreferredTextZoom();
    const sub = CapApp.addListener('resume', () => { applyPreferredTextZoom(); });
    return () => { sub.then((h) => h.remove()); };
  }, []);

  // Universal Links: when the app is opened via https://app.strokone.com/...
  // extract the path and navigate to it inside the app.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cleanup: (() => void) | undefined;
    CapApp.addListener('appUrlOpen', ({ url }) => {
      try {
        const parsed = new URL(url);
        if (parsed.hostname === 'app.strokone.com' && parsed.pathname) {
          router.navigate(parsed.pathname + parsed.search + parsed.hash);
        }
      } catch {
        // malformed URL — ignore
      }
    }).then(handle => {
      cleanup = () => handle.remove();
    });
    return () => cleanup?.();
  }, []);

  // Create a default context - will be updated when user auth is available
  const initialContext = {
    userId: '', // Will be updated by components when auth is available
    sessionId: drillDownService.generateSessionId()
  };

  return (
    <DrillDownProvider initialContext={initialContext}>
      <Suspense fallback={<AppLoadingFallback />}>
        <RouterProvider router={router} />
      </Suspense>
    </DrillDownProvider>
  );
};

// ADDED: Cache persister (Batch 2.3, Task 2.3.2)
// Persists React Query cache to localStorage for offline support
const CACHE_KEY = 'STROKON_REACT_QUERY_V4'; // v4: Discard stale quest fallback data that poisoned earlier caches

const persister: Persister = {
  persistClient: async (client: PersistedClient) => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(client));
        // Clean up old cache versions
        localStorage.removeItem('STROKON_REACT_QUERY_V2');
        localStorage.removeItem('STROKON_REACT_QUERY_V3');
        localStorage.removeItem('TOURNAMENT_CACHE');
      } catch {
        // Storage full or disabled - silently ignore
      }
    }
  },
  restoreClient: async () => {
    if (typeof window !== 'undefined') {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        return cached ? JSON.parse(cached) : undefined;
      } catch {
        return undefined;
      }
    }
    return undefined;
  },
  removeClient: async () => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(CACHE_KEY);
      } catch {
        // Silently ignore
      }
    }
  },
};

function App() {
  // Create QueryClient inside component to prevent hot reload issues
  const [queryClient] = useState(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    return new QueryClient({
      defaultOptions: {
        queries: {
          retry: 1, // Keep retry enabled (retry: 0 was too aggressive and broke requests)
          refetchOnWindowFocus: false,
          staleTime: 1000 * 60 * 5, // 5 minutes
          // Standard production cache times - React Query handles cleanup automatically
          // Mobile: 30min (standard for mobile apps), Desktop: 24h
          gcTime: isMobile ? 1000 * 60 * 30 : 1000 * 60 * 60 * 24,
          // MOBILE FIX: Disable all background polling on mobile to prevent Safari network stack crashes
          refetchInterval: isMobile ? false : undefined,
          refetchIntervalInBackground: false,
        },
      },
    });
  });

  // Email scheduling is now handled server-side via Supabase Cron + Edge Functions
  // No client-side scheduler needed - this prevents race conditions and email spam

  // Performance measurement - runs after React mount
  useEffect(() => {
    // Mark: React mounted
    PerformanceTiming.mark('react-mounted');
    PerformanceTiming.measure('parse-to-mount', 'app-start', 'react-mounted');

    if (import.meta.env.DEV) {
      console.timeEnd('🔍 REACT-RENDER');
      console.log('📊 Performance Summary:');
      console.log('- Check browser DevTools Performance tab');
      console.log('- Look for timing logs above');
      
      // Start memory monitor (Batch 4.1.2, Task 4.1.2.2)
      const stopMemoryMonitor = startMemoryMonitor(5000);
      return stopMemoryMonitor;
    }
  }, [queryClient]);

  // Clean up old service worker caches on mount (one-time cleanup)
  useEffect(() => {
    const cleanupOldServiceWorkerCaches = async () => {
      try {
        const cacheNames = await caches.keys();
        const oldCaches = cacheNames.filter(name =>
          !name.includes('golf-app-v2') && // Keep current cache
          !name.includes('workbox') // Keep workbox caches
        );

        for (const cacheName of oldCaches) {
          await caches.delete(cacheName);
          console.log('[SW-CLEANUP] Deleted old cache:', cacheName);
        }
      } catch (err) {
        console.warn('[SW-CLEANUP] Error:', err);
      }
    };

    // Run once on mount after 5 seconds
    const timeoutId = setTimeout(cleanupOldServiceWorkerCaches, 5000);
    return () => clearTimeout(timeoutId);
  }, []);

  // DEFENSE LAYER 1: Handle password recovery redirect from any page
  useEffect(() => {
    const hash = window.location.hash;
    const pathname = window.location.pathname;

    // Check if this is a recovery link
    if (hash && hash.includes('type=recovery')) {
      console.log('🔍 [RECOVERY] Recovery token detected on path:', pathname);

      // DEFENSE LAYER 2: Business Logic - Only redirect if not already on reset-password page
      if (pathname !== '/reset-password') {
        console.log('🔍 [RECOVERY] Redirecting to reset-password page');
        console.log('🔍 [RECOVERY] Hash preserved:', hash.substring(0, 50) + '...');

        // Redirect to reset-password page while preserving the hash
        window.location.href = '/reset-password' + hash;
      } else {
        console.log('✅ [RECOVERY] Already on reset-password page');
      }
    }
  }, []);

  return (
    <ErrorBoundary onError={(error, errorInfo) => {
      console.error('App-level error caught:', error, errorInfo);
      // You can add error reporting service here (e.g., Sentry)
    }}
    >
      {/* <SafariWarningBanner /> */}
      {/* Honour the iOS Reduce Motion setting in every framer-motion animation */}
      <MotionConfig reducedMotion="user">
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          maxAge: 1000 * 60 * 60 * 24, // 24 hours
          dehydrateOptions: {
            // Only persist queries that have real data.
            // status==='success' alone is not sufficient — every service in this
            // codebase returns errors as ServiceResponse { success:false, data:null }
            // which React Query records as status='success'. Those null-data entries
            // were being frozen into localStorage and restored on mobile app resume,
            // causing pages to show empty states until the next successful refetch.
            shouldDehydrateQuery: (query) => {
              if (query.state.status !== 'success') return false;
              const d = query.state.data as any;
              if (d == null) return false;
              // Reject ServiceResponse-shaped objects that carry a service error
              if (typeof d === 'object' && 'success' in d && d.success === false) return false;
              return true;
            },
          },
        }}
      >
        <OnlineStatusProvider>
          <AuthProvider>
            <DashboardFiltersProvider>
              <ClubProvider>
                <PerformanceZonesProvider>
                  <LanguageProvider>
                    <NotificationsProvider>
                      <AppWithDrillDownProvider />
                      <PendingQRClaimHandler />
                      <Toaster />
                      <PerformanceDebugger />
                      <WebVitalsTracker />
                      <GlobalErrorMonitor />
                    </NotificationsProvider>
                  </LanguageProvider>
                </PerformanceZonesProvider>
              </ClubProvider>
            </DashboardFiltersProvider>
          </AuthProvider>
        </OnlineStatusProvider>
      </PersistQueryClientProvider>
      </MotionConfig>
    </ErrorBoundary>
  );
}

export default App;